import React, {useEffect, useState} from 'react';
import {supabase} from '../../supabase';
import {
    Box,
    Breadcrumbs,
    Card,
    CardActionArea,
    CardMedia,
    Grid,
    ImageList,
    ImageListItem,
    Link,
    Pagination,
    Typography,
} from '@mui/material';
import useDownloadImage from "./useDownloadImage";
import placeholder from "/assets/imgs/logo.png";
import InitializeApp from "../../components/InitializeApp";
import Folder from "@mui/icons-material/Folder";

// Define types for Supabase file and folder objects
type SupabaseFile = {
    id: string;
    name: string;
};

type SupabaseFolder = {
    name: string;
};

// Props for the ImageBrowser component
interface ImageBrowserProps {
    onSelectImage: (imageUrl: string) => void;
    bucket: string
}

const ImageItem = ({url, bucket}: { url: string, bucket: string }) => {

    const image = useDownloadImage(bucket, url)
    if (!image) {
        return <CardMedia
            component="img"
            height="160"
            image={placeholder}
            alt={`placeholder`}
        />
    }
    return <CardMedia
        component="img"
        sx={{width: 140, height: 160, minWidth: 160, objectFit: "cover",}}
        image={image}
        alt={`Image ${bucket} ${url}`}
    />
}

const ImageBrowser: React.FC<ImageBrowserProps> = ({onSelectImage, bucket}) => {
    const [images, setImages] = useState<SupabaseFile[]>([]); // List of image URLs in the current folder
    const [folders, setFolders] = useState<SupabaseFolder[]>([]); // List of subfolders in the current folder
    const [currentFolder, setCurrentFolder] = useState<string>(''); // Current folder path
    const [loading, setLoading] = useState<boolean>(true);
    const [page, setPage] = useState<number>(1); // Current page for pagination
    const [totalPages, setTotalPages] = useState<number>(1); // Total number of pages

    const itemsPerPage = 12; // Number of images per page

    // Fetch folders and images from Supabase storage
    const fetchData = async (folderPath: string = '', page: number = 1) => {
        setLoading(true);
        try {
            // Fetch folders
            const {data: folderData, error: folderError} = await supabase
                .storage
                .from(bucket)
                .list(folderPath, {
                    limit: 100,
                    offset: 0,
                    sortBy: {column: 'name', order: 'asc'},
                });

            if (folderError) throw folderError;

            // Filter out folders (items with no `id` are folders)
            const subfolders = folderData.filter((item) => !item.id) as SupabaseFolder[];
            setFolders(subfolders);

            // Fetch images
            const {data: imageData, error: imageError} = await supabase
                .storage
                .from(bucket)
                .list(folderPath, {
                    limit: itemsPerPage,
                    offset: (page - 1) * itemsPerPage,
                    sortBy: {column: 'name', order: 'asc'},
                });


            if (imageError) throw imageError;

            setImages(imageData.filter(i => i.id !== null))

            // Calculate total pages for pagination
            //@ts-ignore
            const {count: totalItems} = await supabase
                .storage
                .from(bucket)
                .list(folderPath, {
                    limit: 1000, // Adjust based on your needs
                });
            console.log(totalItems)
            setTotalPages(Math.ceil(totalItems / itemsPerPage));
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle folder navigation
    const handleFolderClick = (folderName: string) => {
        const newFolderPath = currentFolder ? `${currentFolder}/${folderName}` : folderName;
        setCurrentFolder(newFolderPath);
        setPage(1); // Reset to the first page when navigating to a new folder
        fetchData(newFolderPath, 1);
    };

    // Handle pagination
    const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
        setPage(newPage);
        fetchData(currentFolder, newPage);
    };

    // Handle breadcrumb navigation
    const handleBreadcrumbClick = (index: number) => {
        const newFolderPath = currentFolder.split('/').slice(0, index + 1).join('/');
        setCurrentFolder(newFolderPath);
        setPage(1);
        fetchData(newFolderPath, 1);
    };

    // Fetch data on component mount or when currentFolder changes
    useEffect(() => {
        fetchData(currentFolder, page);
    }, [currentFolder, page]);

    // Render breadcrumbs
    const renderBreadcrumbs = () => {
        const pathParts = currentFolder.split('/').filter((part) => part);
        return (
            <Breadcrumbs aria-label="breadcrumb">
                <Link
                    underline="hover"
                    color="inherit"
                    onClick={() => handleBreadcrumbClick(-1)}
                    style={{cursor: 'pointer'}}
                >
                    Root
                </Link>
                {pathParts.map((part, index) => (
                    <Link
                        key={index}
                        underline="hover"
                        color="inherit"
                        onClick={() => handleBreadcrumbClick(index)}
                        style={{cursor: 'pointer'}}
                    >
                        {part}
                    </Link>
                ))}
            </Breadcrumbs>
        );
    };

    return (
        <div>
            {/* Breadcrumbs for folder navigation */}
            {renderBreadcrumbs()}

            {/* Folders */}
            <Grid container spacing={2} style={{marginTop: '16px'}}>
                {folders.map((folder, index) => (
                    <Grid item key={index} xs={12} sm={6} md={4} lg={3}>
                        <Card>
                            <CardActionArea onClick={() => handleFolderClick(folder.name)}>
                                <Box sx={{textAlign: "center"}}>
                                    <Folder/>
                                </Box>
                                <Typography variant="subtitle1" align="center">
                                    {folder.name}
                                </Typography>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Images */}
            {!loading && <ImageList sx={{width: 500, height: 450}} cols={3} rowHeight={164}>
                {images.map((imageUrl, index) => (
                    <ImageListItem key={index} onClick={() => onSelectImage(currentFolder + "/" + imageUrl.name)}>
                        <ImageItem bucket={bucket} url={currentFolder + "/" + imageUrl.name}/>
                    </ImageListItem>
                ))}
            </ImageList>}
            {loading && <Box sx={{width: 500, height: 450}}><InitializeApp mt={"1em"}/></Box>}

            {/* Pagination */}
            <div style={{display: 'flex', justifyContent: 'center', marginTop: '24px'}}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                />
            </div>

            {/* Loading state */}
        </div>
    );
};

export default ImageBrowser;